import React, {
  useEffect,
  createContext,
  useContext,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { usePersistState } from 'helper';
import MaskNames from 'modules/reports/containers/MaskNames';
import {
  useBalanceList,
  useClientList,
  useExportBalance,
  useExportClient,
  useExportRegistration,
  useExportSenderName,
  useExportSmsUsage,
  useExportTransactions,
  useRegistrationList,
  useSenderNameList,
  useSmsUsageList,
  useTransactionList,
} from 'modules/reports/hooks';
import SmsUsage from 'modules/reports/containers/SmsUsage';
import PaymentTransactions from 'modules/reports/containers/PaymentTransactions';
import SubscriberBalance from 'modules/reports/containers/SubscriberBalance';
import { omit, pickBy } from 'lodash';
import ClientInformation from 'modules/reports/containers/ClientInformation';
import RegistrationList from 'modules/reports/containers/RegistrationReport';

const ReportsContext = createContext();

const config = {
  'client-information': {
    title: 'Clients Information',
    exportFileName: 'Clients_Information',
    component: ClientInformation,
    hook: useClientList,
    exportHook: useExportClient,
  },
  'mask-name': {
    title: 'Mask Name',
    exportFileName: 'Mask_Names',
    component: MaskNames,
    hook: useSenderNameList,
    exportHook: useExportSenderName,
  },
  'sms-usage': {
    title: 'SMS Usage Count and SMS Total Amount Consumed',
    exportFileName: 'SMS_Usage_Consumption',
    component: SmsUsage,
    hook: useSmsUsageList,
    exportHook: useExportSmsUsage,
    withFilter: true,
  },
  'payment-transactions': {
    title: `SMS Request`,
    exportFileName: 'SMS_Request',
    component: PaymentTransactions,
    hook: useTransactionList,
    exportHook: useExportTransactions,
    withFilter: true,
  },
  'current-balance': {
    title: `SMS Current Balance`,
    exportFileName: 'Agency_Balance',
    component: SubscriberBalance,
    hook: useBalanceList,
    exportHook: useExportBalance,
  },
  'registration-report': {
    title: 'Registration report (Total registered and total approved)',
    exportFileName: 'Registration_Report',
    component: RegistrationList,
    hook: useRegistrationList,
    exportHook: useExportRegistration,
    withFilter: true,
  },
};

function ReportsProvider({ children }) {
  const [filter, setFilter] = useState({
    from: '',
    to: '',
  });
  const [page, setPage] = useState({ page: '' });
  const payload = useMemo(
    () =>
      pickBy({
        date_from: filter?.from,
        date_to: filter?.to,
        ...page,
      }),
    [filter, page],
  );

  const [current, setCurrent] = usePersistState(
    'reports-selected',
    {
      type: { label: 'Clients Information', value: 'client-information' },
    },
    {
      keepOnUnmount: false,
    },
  );

  const currentConfig = useMemo(
    () => config?.[current?.type?.value],
    [current?.type?.value],
  );

  const { hook, exportHook } = currentConfig;
  const [isLoading, list, meta] = hook(payload);
  const [isExporting, exportData] = exportHook(omit(payload, ['page']));

  useEffect(() => {
    setPage({ page: '' });
  }, [filter, current]);

  useEffect(() => {
    setFilter({ from: '', to: '' });
  }, [current]);

  const valueObject = useMemo(
    () => ({
      current,
      setCurrent,
      currentConfig,
      isLoading,
      list,
      meta,
      setPage,
      setFilter,
      isExporting,
      exportData,
      payload,
    }),
    [
      payload,
      current,
      setCurrent,
      currentConfig,
      isLoading,
      list,
      meta,
      setFilter,
      isExporting,
      exportData,
    ],
  );

  return (
    <ReportsContext.Provider value={valueObject}>
      {children}
    </ReportsContext.Provider>
  );
}

const useReportsContext = () => useContext(ReportsContext);

ReportsProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
    PropTypes.string,
  ]).isRequired,
};

export { ReportsProvider, useReportsContext };
